import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Package, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { Loading } from '../../components/Loading';
import { toast } from 'sonner';
import { getReservations } from '../../utils/firestoreService';

export const AdminReservations = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchReservations();
    }
  }, [user]);

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const data = await getReservations();
      setReservations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      toast.error('Error al cargar las compras');
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-8">
      <h1 className="text-4xl font-fredoka font-bold text-[#1A1A2E] mb-8" data-testid="admin-reservations-title">
        Compras
      </h1>

      {reservations.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
          <Package className="w-16 h-16 mx-auto mb-4 text-[#9CA3AF]" />
          <p className="text-xl text-[#4A4A6A] font-quicksand" data-testid="no-reservations-message">
            No hay compras aún
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {reservations.map((reservation) => (
            <div
              key={reservation.id}
              className="bg-white rounded-2xl p-6 shadow-lg"
              data-testid={`reservation-card-${reservation.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Customer Info */}
                <div className="lg:col-span-1">
                  <h3 className="font-fredoka font-bold text-lg text-[#1A1A2E] mb-4">
                    Información del Cliente
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Mail className="w-5 h-5 text-[#00C2CB] mt-0.5" />
                      <div>
                        <p className="text-sm text-[#9CA3AF] font-quicksand">Email</p>
                        <p className="font-quicksand text-[#1A1A2E]">{reservation.customerInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Phone className="w-5 h-5 text-[#00C2CB] mt-0.5" />
                      <div>
                        <p className="text-sm text-[#9CA3AF] font-quicksand">Teléfono</p>
                        <p className="font-quicksand text-[#1A1A2E]">{reservation.customerInfo.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-[#00C2CB] mt-0.5" />
                      <div>
                        <p className="text-sm text-[#9CA3AF] font-quicksand">Dirección</p>
                        <p className="font-quicksand text-[#1A1A2E]">{reservation.customerInfo.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Calendar className="w-5 h-5 text-[#00C2CB] mt-0.5" />
                      <div>
                        <p className="text-sm text-[#9CA3AF] font-quicksand">Fecha</p>
                        <p className="font-quicksand text-[#1A1A2E]">
                          {new Date(reservation.createdAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div className="lg:col-span-2">
                  <h3 className="font-fredoka font-bold text-lg text-[#1A1A2E] mb-4">Productos</h3>
                  <div className="space-y-3">
                    {reservation.products.map((product, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center bg-slate-50 rounded-xl p-4"
                        data-testid={`reservation-product-${idx}`}
                      >
                        <div>
                          <p className="font-quicksand font-semibold text-[#1A1A2E]">
                            {product.productName}
                          </p>
                          <p className="text-sm text-[#4A4A6A] font-quicksand">
                            Cantidad: {product.quantity}
                          </p>
                        </div>
                        <p className="font-fredoka font-bold text-[#00C2CB]">
                          ${(product.price * product.quantity).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-slate-200 flex justify-between items-center">
                    <span className="font-fredoka font-bold text-xl text-[#1A1A2E]">Total</span>
                    <span className="font-fredoka font-bold text-3xl text-[#FF6B00]" data-testid={`reservation-total-${reservation.id}`}>
                      ${reservation.totalAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};